import { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { BackButton, NextButton } from "src/components/application/styled-components/Buttons";
import {
  AdhocKycAuth,
  AdhocKycContextResponse,
  AdhocKycHeldDetails,
  adhocKycErrorMessage,
  useAdhocKycConfirmDetailsMutation,
} from "src/store/features/api/adhocKyc";
import {
  AdhocKycBody,
  AdhocKycError,
  AdhocKycHeading,
  AdhocKycMatchNotice,
  ADHOC_KYC_NAVY,
} from "./AdhocKycShell";
import UpdateDetailsForm from "./UpdateDetailsForm";

export interface DetailsStepProps {
  auth: AdhocKycAuth;
  context: AdhocKycContextResponse;
}

const COUNTRY_LABEL: Record<string, string> = { AU: "Australia", NZ: "New Zealand" };

/** Label/value rows for the masked held details; empty optional values are dropped. */
export const heldDetailRows = (
  held: AdhocKycHeldDetails,
  residencyCountry: string | undefined,
): Array<[string, string]> =>
  (
    [
      ["Name", held.fullName],
      ["Middle name", held.middleName],
      ["Date of birth", held.dobMasked],
      ["Address", held.addressMasked],
      ["Phone", held.phoneMasked],
      ["Country of residence", COUNTRY_LABEL[String(residencyCountry)]],
      ["Citizenship", held.citizenship],
      ["ID document", held.documentMasked],
    ] as Array<[string, string | undefined]>
  ).filter((row): row is [string, string] => Boolean(row[1]));

/**
 * Step 2. Shows what we hold (masked server-side) and asks the customer to
 * attest it is current. "Something has changed" — or a server-side
 * `sufficient: false` — switches to the update form, which is the stale
 * branch that later requires the Onfido re-check.
 */
export default function DetailsStep({ auth, context }: DetailsStepProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [confirm, { isLoading, error }] = useAdhocKycConfirmDetailsMutation();
  const held = context.heldDetails;
  // A re-opened row (in-flow retry / ops re-check) has nothing to "confirm":
  // the form names what the issuer rejected and takes the corrected details.
  const canConfirm = context.sufficient !== false && Boolean(held) && !context.priorAttempt;

  if (editing || !canConfirm) {
    return (
      <UpdateDetailsForm
        auth={auth}
        context={context}
        onBack={canConfirm ? () => setEditing(false) : undefined}
      />
    );
  }

  const rows = heldDetailRows(held as AdhocKycHeldDetails, context.residencyCountry);

  return (
    <Box data-testid="adhoc-kyc-details-step">
      <AdhocKycHeading>Are these details still current?</AdhocKycHeading>
      <AdhocKycBody>
        Some details are partly hidden for your security. If anything below is out of date, choose
        &ldquo;Something has changed&rdquo; and we&rsquo;ll ask you to update it.
      </AdhocKycBody>
      <AdhocKycMatchNotice />
      <Box
        component="dl"
        data-testid="adhoc-kyc-held-details"
        sx={{
          border: "1px solid #E5E7EB",
          borderRadius: "16px",
          bgcolor: "#FFFFFF",
          p: 2,
          m: 0,
          mb: 2,
        }}
      >
        {rows.map(([label, value]) => (
          <Box key={label} sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}>
            <Typography component="dt" sx={{ fontSize: "14px", color: "#6B7280" }}>
              {label}
            </Typography>
            <Typography
              component="dd"
              sx={{ fontSize: "14px", fontWeight: 600, color: ADHOC_KYC_NAVY, m: 0 }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
      {held?.middleName ? null : (
        <Typography
          data-testid="adhoc-kyc-middle-name-hint"
          sx={{ fontSize: "13px", color: "#6B7280", mb: 2 }}
        >
          Does your ID show a middle name? It is not listed above, so choose &ldquo;Something has
          changed&rdquo; and add it — the check compares your name exactly as printed.
        </Typography>
      )}
      {error ? <AdhocKycError message={adhocKycErrorMessage(error)} /> : null}
      <Stack spacing={1.5}>
        <NextButton
          fullWidth
          disabled={isLoading}
          onClick={() => confirm({ auth, body: { attestCurrent: true } })}
          data-testid="adhoc-kyc-details-confirm"
        >
          These are correct
        </NextButton>
        <BackButton
          fullWidth
          onClick={() => setEditing(true)}
          data-testid="adhoc-kyc-details-change"
        >
          Something has changed
        </BackButton>
      </Stack>
    </Box>
  );
}
